import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { MarketplaceFacade } from '../../marketplace/marketplace-v2/marketplace.facade';
import { MarketplaceV2Query } from '../../marketplace/marketplace-v2/marketplace-v2.query';
import { MarketplaceItemV2 } from '../../marketplace/marketplace-v2/models/marketplace-item-v2.model';
import { MarketplaceExtendedQuery } from '../../marketplace/marketplace.extended.query';
import { MarketplaceService } from '../../marketplace/marketplace.service';
import { ProcessProviderId } from '../../marketplace/models/process-provider-id';
import { LoyaltyStatusService } from '../loyalty-status.service';
import { MixedLoyaltyItem } from '../models/mixed-loyalty-item.model';
import { REWARD_STATUS } from '../models/reward-status.enum';
import { LoyaltySource } from './loyalty-source.model';
import { mapRewardStatusToLoyaltyStatus } from './reward-status.mapper';

/**
 * Marketplace sources for `MixedLoyaltyQuery`, one per version. Registered against
 * `MARKETPLACE_SOURCE` with `provideVersionedService`, so `componentVersions.marketplace` selects
 * which is used.
 */

/** v1: the Akita-backed extended query, with status derived from item flags. */
@Injectable({ providedIn: 'root' })
export class MarketplaceV1Source implements LoyaltySource {
  private marketplaceExtendedQuery = inject(MarketplaceExtendedQuery);
  private marketplaceService = inject(MarketplaceService);
  private statusService = inject(LoyaltyStatusService);

  fetch(): Observable<null> {
    return this.marketplaceService.fetchMarketplaceItems();
  }

  selectFavourites(showSoldOut: boolean): Observable<MixedLoyaltyItem[]> {
    return this.marketplaceExtendedQuery.selectExtendedMarketplaceFavourites().pipe(
      map((items) =>
        (items ?? [])
          .filter((x) => showSoldOut || x.AvailabilityCount > 0)
          .map((x) => new MixedLoyaltyItem({ type: 'marketplace', data: x, status: this.statusService.getStatus(x) }))
      )
    );
  }

  selectAll(filterTypes: ProcessProviderId[]): Observable<MixedLoyaltyItem[]> {
    return this.marketplaceExtendedQuery
      .selectExtendedMarketplaceItemsOfType(...filterTypes)
      .pipe(
        map((items) =>
          (items ?? []).map((x) => new MixedLoyaltyItem({ type: 'marketplace', data: x, status: this.statusService.getStatus(x) }))
        )
      );
  }
}

/** v2: the signal-store query and facade, with REWARD_STATUS mapped to a state. */
@Injectable({ providedIn: 'root' })
export class MarketplaceV2Source implements LoyaltySource {
  private marketplaceV2Query = inject(MarketplaceV2Query);
  private marketplaceFacade = inject(MarketplaceFacade);

  /** The facade's rxMethod is fire-and-forget, so this completes immediately. */
  fetch(): Observable<null> {
    this.marketplaceFacade.fetchMarketplaceItems();
    return of(null);
  }

  selectFavourites(showSoldOut: boolean): Observable<MixedLoyaltyItem[]> {
    return this.marketplaceV2Query.selectFavourites().pipe(
      map((items: MarketplaceItemV2[]) =>
        (items ?? [])
          .filter((x) => showSoldOut || x.Status !== REWARD_STATUS.SoldOut)
          .map((x) => new MixedLoyaltyItem({ type: 'marketplace', data: x, status: mapRewardStatusToLoyaltyStatus(x.Status) }))
      )
    );
  }

  selectAll(filterTypes: ProcessProviderId[]): Observable<MixedLoyaltyItem[]> {
    return this.marketplaceV2Query.selectMarketplaceItemsOfType(filterTypes).pipe(
      map((items: MarketplaceItemV2[]) =>
        (items ?? []).map(
          (x) => new MixedLoyaltyItem({ type: 'marketplace', data: x, status: mapRewardStatusToLoyaltyStatus(x.Status) })
        )
      )
    );
  }
}
